import { AcademyCourse, CourseAccessRole, CourseMember } from '../types';

export function canSeeAllCourses(options: {
  isSuperAdmin?: boolean;
  isCourseAdmin?: boolean;
}): boolean {
  return options.isSuperAdmin === true || options.isCourseAdmin === true;
}

export function buildCreatorCourseAccess(
  uid: string,
  displayName?: string,
  email?: string,
  grantedAt: string = new Date().toISOString()
): { members: Record<string, CourseMember>; accessUserIds: string[] } {
  const member: CourseMember = {
    uid,
    role: 'manager',
    displayName: displayName || '',
    email: email || '',
    grantedAt,
  };
  return {
    members: { [uid]: member },
    accessUserIds: [uid],
  };
}

const COURSE_ACCESS_ROLES: CourseAccessRole[] = ['manager', 'collaborator', 'reviewer'];

export function getCourseRole(
  course: AcademyCourse,
  uid: string | undefined
): CourseAccessRole | null {
  if (!uid) return null;
  const memberRole = course.members?.[uid]?.role;
  if (memberRole && COURSE_ACCESS_ROLES.includes(memberRole)) {
    return memberRole;
  }
  if (course.createdBy === uid) {
    return 'manager';
  }
  if (course.accessUserIds?.includes(uid)) {
    return 'collaborator';
  }
  return null;
}

export function canEditCourseContent(
  course: AcademyCourse,
  uid: string | undefined,
  canSeeAll: boolean
): boolean {
  if (canSeeAll) return true;
  const role = getCourseRole(course, uid);
  return role === 'manager' || role === 'collaborator';
}

/** Studio list / review visibility — any assigned course role. */
export function canAccessCourse(
  course: AcademyCourse,
  uid: string | undefined,
  canSeeAll: boolean
): boolean {
  if (canSeeAll) return true;
  return getCourseRole(course, uid) !== null;
}

/** Learner Deep Dive: published courses are public; drafts require a course role. */
export function canViewLearnerCourse(
  course: AcademyCourse,
  uid: string | undefined,
  canSeeAll: boolean
): boolean {
  if (course.status === 'Published') return true;
  return canAccessCourse(course, uid, canSeeAll);
}

export function canManageCourseAccess(
  course: AcademyCourse,
  uid: string | undefined,
  canSeeAll: boolean
): boolean {
  if (canSeeAll) return true;
  return getCourseRole(course, uid) === 'manager';
}

export function canDeleteCourse(
  course: AcademyCourse,
  uid: string | undefined,
  canSeeAll: boolean
): boolean {
  return canManageCourseAccess(course, uid, canSeeAll);
}

export function listCourseMembers(course: AcademyCourse): CourseMember[] {
  if (course.members && Object.keys(course.members).length > 0) {
    return Object.values(course.members);
  }
  if (course.createdBy) {
    return [{
      uid: course.createdBy,
      role: 'manager',
      displayName: course.createdByName,
      email: course.createdByEmail,
    }];
  }
  return [];
}

export function courseAccessRoleLabel(role: CourseAccessRole): string {
  if (role === 'manager') return 'Course Manager';
  if (role === 'reviewer') return 'Course Reviewer';
  return 'Course Collaborator';
}
